import { useState, useEffect } from 'react'

const BASE_PRICE_USD = 14.99
const BASE_ORIGINAL_USD = 60

interface CurrencyInfo {
  code: string
  locale: string
  decimals: number
}

const CURRENCY_MAP: Record<string, CurrencyInfo> = {
  MX: { code: 'MXN', locale: 'es-MX', decimals: 0 },
  CO: { code: 'COP', locale: 'es-CO', decimals: 0 },
  AR: { code: 'ARS', locale: 'es-AR', decimals: 0 },
  CL: { code: 'CLP', locale: 'es-CL', decimals: 0 },
  PE: { code: 'PEN', locale: 'es-PE', decimals: 2 },
  BR: { code: 'BRL', locale: 'pt-BR', decimals: 2 },
  BO: { code: 'BOB', locale: 'es-BO', decimals: 2 },
  PY: { code: 'PYG', locale: 'es-PY', decimals: 0 },
  UY: { code: 'UYU', locale: 'es-UY', decimals: 0 },
  GT: { code: 'GTQ', locale: 'es-GT', decimals: 2 },
  CR: { code: 'CRC', locale: 'es-CR', decimals: 0 },
  DO: { code: 'DOP', locale: 'es-DO', decimals: 0 },
  HN: { code: 'HNL', locale: 'es-HN', decimals: 2 },
  NI: { code: 'NIO', locale: 'es-NI', decimals: 2 },
  SV: { code: 'USD', locale: 'es-SV', decimals: 2 },
  ES: { code: 'EUR', locale: 'es-ES', decimals: 2 },
  DE: { code: 'EUR', locale: 'de-DE', decimals: 2 },
  FR: { code: 'EUR', locale: 'fr-FR', decimals: 2 },
  IT: { code: 'EUR', locale: 'it-IT', decimals: 2 },
  PT: { code: 'EUR', locale: 'pt-PT', decimals: 2 },
  GB: { code: 'GBP', locale: 'en-GB', decimals: 2 },
  CA: { code: 'CAD', locale: 'en-CA', decimals: 2 },
  AU: { code: 'AUD', locale: 'en-AU', decimals: 2 },
}

const USD_COUNTRIES = new Set(['EC', 'PA', 'VE', 'US'])

function fmt(amount: number, info: CurrencyInfo): string {
  return new Intl.NumberFormat(info.locale, {
    style: 'currency',
    currency: info.code,
    minimumFractionDigits: info.decimals,
    maximumFractionDigits: info.decimals,
  }).format(amount)
}

function fetchWithTimeout(url: string, ms: number): Promise<Response> {
  const controller = new AbortController()
  const id = setTimeout(() => controller.abort(), ms)
  return fetch(url, { signal: controller.signal }).finally(() =>
    clearTimeout(id),
  )
}

export interface PriceData {
  price: string
  originalPrice: string
  currencyCode: string
  loading: boolean
}

const USD_DEFAULT: PriceData = {
  price: '$14.99 USD',
  originalPrice: '$60 USD',
  currencyCode: 'USD',
  loading: false,
}

export function useCurrencyPrice(): PriceData {
  const [data, setData] = useState<PriceData>({ ...USD_DEFAULT, loading: true })

  useEffect(() => {
    let cancelled = false

    async function detect() {
      try {
        // Step 1: detect country via ipwho.is (reliable, no key, no rate limit issues)
        let countryCode = 'US'
        try {
          const geoRes = await fetchWithTimeout('https://ipwho.is/', 5000)
          const geo = await geoRes.json()
          if (geo.success && geo.country_code) {
            countryCode = geo.country_code
          }
        } catch {
          // Try fallback geo service
          try {
            const geoRes2 = await fetchWithTimeout(
              'https://ipapi.co/json/',
              5000,
            )
            const geo2 = await geoRes2.json()
            if (geo2.country_code) countryCode = geo2.country_code
          } catch {
            // Both failed, use USD
          }
        }

        if (cancelled) return

        // USD countries — show USD
        if (USD_COUNTRIES.has(countryCode)) {
          if (!cancelled) setData(USD_DEFAULT)
          return
        }

        const currencyInfo = CURRENCY_MAP[countryCode]

        // No mapping for this country → USD
        if (!currencyInfo || currencyInfo.code === 'USD') {
          if (!cancelled) setData(USD_DEFAULT)
          return
        }

        // Step 2: get exchange rates
        const ratesRes = await fetchWithTimeout(
          'https://open.er-api.com/v6/latest/USD',
          5000,
        )
        const ratesData = await ratesRes.json()
        const rate: number = ratesData?.rates?.[currencyInfo.code]

        if (!rate || cancelled) {
          if (!cancelled) setData(USD_DEFAULT)
          return
        }

        setData({
          price: fmt(BASE_PRICE_USD * rate, currencyInfo),
          originalPrice: fmt(BASE_ORIGINAL_USD * rate, currencyInfo),
          currencyCode: currencyInfo.code,
          loading: false,
        })
      } catch {
        if (!cancelled) setData(USD_DEFAULT)
      }
    }

    detect()
    return () => {
      cancelled = true
    }
  }, [])

  return data
}
